import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "../mock";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("adl_lang") || "fr");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("adl_cart")) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("adl_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("adl_cart", JSON.stringify(cart));
  }, [cart]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.fr;
    return (key) => dict[key] ?? key;
  }, [lang]);

  // localized field helper: obj = {fr, en, zh}
  const tl = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] ?? obj.fr;
  };

  const addToCart = (product, size = null, color = null, qty = 1, flavor = null) => {
    setCart((prev) => {
      const key = `${product.id}-${size || ""}-${color || ""}-${flavor || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          brand: product.brand,
          price: product.price,
          image: product.image,
          category: product.category,
          tiers: product.tiers || null,
          size,
          color,
          flavor,
          qty,
        },
      ];
    });
  };

  // Auto volume/tier pricing for Négoce items (best qualifying tier for the quantity)
  const itemUnitPrice = (item) => {
    if (item.category === "negoce" && item.tiers?.length) {
      let price = item.price;
      [...item.tiers].sort((a, b) => a.qty - b.qty).forEach((tr) => { if (item.qty >= tr.qty) price = tr.price; });
      return price;
    }
    return item.price;
  };

  const updateQty = (key, qty) => {
    setCart((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  };

  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + itemUnitPrice(i) * i.qty, 0);

  const value = {
    lang,
    setLang,
    t,
    tl,
    cart,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    cartCount,
    cartTotal,
    itemUnitPrice,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
